import React, { useState, useEffect } from "react";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";

interface Metric {
  label: string;
  value: string;
  suffix: string;
}

interface Subject {
  name: string;
  present: number;
  total: number;
  color: "success" | "primary" | "warning";
}

const HeroSection: React.FC = () => {
  const [currentMetric, setCurrentMetric] = useState<number>(0);
  const [isVisible, setIsVisible] = useState<boolean>(false);

  const metrics: Metric[] = [
    { label: "What You Get", value: "Unified Operations", suffix: "" },
    { label: "How It Works", value: "Configurable Workflows", suffix: "" },
    { label: "Who It Fits", value: "Adaptable to Your Model", suffix: "" },
  ];

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setCurrentMetric((prev) => (prev + 1) % metrics?.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [metrics?.length]);

  const handleDemoClick = (): void => {
    const demoSection = document.getElementById("demo-booking");
    if (demoSection) {
      demoSection?.scrollIntoView({ behavior: "smooth" });
    }
  };

  // const handleTourClick = (): void => {
  //   const tourSection = document.getElementById("product-tour");
  //   if (tourSection) {
  //     tourSection?.scrollIntoView({ behavior: "smooth" });
  //   }
  // };

  const subjects: Subject[] = [
    { name: "Computer Science", present: 45, total: 48, color: "success" },
    { name: "Mathematics", present: 52, total: 55, color: "primary" },
    { name: "Physics", present: 38, total: 42, color: "warning" },
  ];

  const chartHeights: number[] = [40, 65, 45, 80, 55, 90, 70];

  return (
    <section
      id="overview"
      className="relative min-h-screen bg-gradient-to-br from-background via-surface to-background overflow-hidden scroll-mt-24"
    >
      {/* Aurora Background Effects */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 left-10 w-96 h-96 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-r from-accent/20 to-primary/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-r from-secondary/10 to-accent/10 rounded-full blur-2xl animate-pulse delay-2000"></div>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-24 sm:pt-32 pb-28 md:pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[calc(100vh-8rem)]">
          {/* Left Content */}
          <div
            className={`space-y-6 sm:space-y-8 transform transition-all duration-1000 ${
              isVisible
                ? "translate-y-0 opacity-100"
                : "translate-y-8 opacity-0"
            }`}
          >
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-xs sm:text-sm font-inter font-600">
              <Icon name="Sparkles" size={16} color="currentColor" />
              <span>Ditch the Chaos. Run the Campus.</span>
            </div>

            {/* Main Headline */}
            <div className="space-y-4">
              <h1 className="font-jakarta font-light text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-foreground leading-tight">
                Transform Your Institution from{" "}
                <span className="bg-gradient-to-r from-primary font-light to-secondary bg-clip-text text-transparent">
                  Spreadsheet Chaos
                </span>{" "}
                to Unified Excellence
              </h1>

              <p className="font-inter font-light text-base sm:text-lg md:text-xl lg:text-2xl text-muted-foreground leading-relaxed max-w-2xl">
                The only education management platform that{" "}
                <span className="text-primary font-light">
                  unifies all institutional operations
                </span>{" "}
                with{" "}
                <span className="text-black font-light">
                  streamlined workflows and efficient processes.
                </span>
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="default"
                size="lg"
                onClick={handleDemoClick}
                className="font-jakarta font-700 text-base sm:text-lg px-8 py-4 aurora-glow hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                iconName="Play"
                iconPosition="left"
                iconSize={20}
              >
                Book Live Demo
              </Button>

              {/* <Button
                variant="outline"
                size="lg"
                onClick={handleTourClick}
                className="font-jakarta font-600 text-lg px-8 py-4 hover:bg-primary/5 transition-all duration-300"
                iconName="MousePointer"
                iconPosition="left"
                iconSize={20}
              >
                Explore Interactive Tour
              </Button> */}
            </div>

            {/* Rolling Metrics */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/20 aurora-glass">
              <div className="flex items-center justify-between">
                <div className="text-center flex-1">
                  <div className="font-jakarta font-800 text-xl sm:text-2xl md:text-3xl text-foreground">
                    {metrics?.[currentMetric]?.value}
                    <span className="text-primary">
                      {metrics?.[currentMetric]?.suffix}
                    </span>
                  </div>
                  <div className="font-inter font-500 text-xs sm:text-sm text-muted-foreground mt-1">
                    {metrics?.[currentMetric]?.label}
                  </div>
                </div>

                <div className="flex space-x-2 ml-6">
                  {metrics?.map((_, index) => (
                    <div
                      key={index}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        index === currentMetric ? "bg-primary" : "bg-muted"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - 3D Dashboard Stack */}
          <div
            className={`relative transform transition-all duration-1000 delay-300 ${
              isVisible
                ? "translate-y-0 opacity-100"
                : "translate-y-8 opacity-0"
            }`}
          >
            <div className="relative">
              {/* Dashboard Stack */}
              <div className="relative space-y-4">
                {/* Top Dashboard - Analytics */}
                <div className="transform rotate-3 hover:rotate-0 transition-transform duration-500 aurora-glass rounded-2xl p-6 border border-white/20">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-jakarta font-700 text-base sm:text-lg text-foreground">
                      Analytics Dashboard
                    </h3>
                    <div className="flex space-x-2">
                      <div className="w-3 h-3 bg-success rounded-full"></div>
                      <div className="w-3 h-3 bg-warning rounded-full"></div>
                      <div className="w-3 h-3 bg-error rounded-full"></div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="bg-primary/10 rounded-lg p-3 text-center">
                      <div className="font-jakarta font-700 text-lg sm:text-xl text-primary">
                        2,847
                      </div>
                      <div className="font-inter font-500 text-xs sm:text-sm text-muted-foreground">
                        Students
                      </div>
                    </div>
                    <div className="bg-success/10 rounded-lg p-3 text-center">
                      <div className="font-jakarta font-700 text-lg sm:text-xl text-success">
                        99.1%
                      </div>
                      <div className="font-inter font-500 text-xs sm:text-sm text-muted-foreground">
                        Attendance
                      </div>
                    </div>
                    <div className="bg-accent/10 rounded-lg p-3 text-center">
                      <div className="font-jakarta font-700 text-lg sm:text-xl text-accent">
                        ₹2.4M
                      </div>
                      <div className="font-inter font-500 text-xs sm:text-sm text-muted-foreground">
                        Fees
                      </div>
                    </div>
                  </div>

                  <div className="h-20 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-lg flex items-end justify-around p-2">
                    {chartHeights?.map((height, index) => (
                      <div
                        key={index}
                        className="bg-gradient-to-t from-primary to-secondary rounded-sm w-4 transition-all duration-1000 delay-500"
                        style={{ height: `${height}%` }}
                      />
                    ))}
                  </div>
                </div>

                {/* Middle Dashboard - Attendance */}
                <div className="transform -rotate-2 hover:rotate-0 transition-transform duration-500 aurora-glass rounded-2xl p-6 border border-white/20 ml-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-jakarta font-700 text-base sm:text-lg text-foreground">
                      Live Attendance
                    </h3>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                      <span className="font-inter font-500 text-xs sm:text-sm text-success">
                        Live
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {subjects?.map((subject, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between"
                      >
                        <div className="flex-1">
                          <div className="font-inter font-600 text-xs sm:text-sm text-foreground">
                            {subject?.name}
                          </div>
                          <div className="w-full bg-muted rounded-full h-2 mt-1">
                            <div
                              className={`h-2 rounded-full bg-${
                                subject?.color
                              } transition-all duration-1000 delay-${
                                index * 200
                              }`}
                              style={{
                                width: `${
                                  (subject?.present / subject?.total) * 100
                                }%`,
                              }}
                            />
                          </div>
                        </div>
                        <div className="ml-4 text-right">
                          <div className="font-jakarta font-700 text-xs sm:text-sm text-foreground">
                            {subject?.present}/{subject?.total}
                          </div>
                          <div className="font-inter font-500 text-[10px] sm:text-xs text-muted-foreground">
                            {Math.round(
                              (subject?.present / subject?.total) * 100
                            )}
                            %
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Bottom Dashboard - Finance */}
                <div className="transform rotate-1 hover:rotate-0 transition-transform duration-500 aurora-glass rounded-2xl p-6 border border-white/20 mr-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-jakarta font-700 text-base sm:text-lg text-foreground">
                      Finance Hub
                    </h3>
                    <Icon
                      name="TrendingUp"
                      size={20}
                      color="var(--color-success)"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="font-inter font-500 text-xs sm:text-sm text-muted-foreground">
                        Monthly Revenue
                      </div>
                      <div className="font-jakarta font-800 text-xl sm:text-2xl text-success">
                        ₹4.2M
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon
                          name="ArrowUp"
                          size={14}
                          color="var(--color-success)"
                        />
                        <span className="font-inter font-600 text-[10px] sm:text-xs text-success">
                          +12.5%
                        </span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="font-inter font-500 text-xs sm:text-sm text-muted-foreground">
                        Pending Fees
                      </div>
                      <div className="font-jakarta font-800 text-xl sm:text-2xl text-warning">
                        ₹0.8M
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon
                          name="ArrowDown"
                          size={14}
                          color="var(--color-success)"
                        />
                        <span className="font-inter font-600 text-[10px] sm:text-xs text-success">
                          -8.3%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 w-8 h-8 bg-primary rounded-full flex items-center justify-center animate-bounce">
                <Icon name="Check" size={16} color="white" strokeWidth={3} />
              </div>

              {/* <div className="absolute -bottom-4 -left-4 w-12 h-12 bg-gradient-to-r from-accent to-secondary rounded-full flex items-center justify-center animate-pulse">
                <Icon name="Zap" size={20} color="white" strokeWidth={2.5} />
              </div> */}
            </div>
          </div>
        </div>
      </div>
      {/* Scroll Indicator */}
      <div className="absolute bottom-6 md:bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-20 pointer-events-none">
        <div className="flex flex-col items-center space-y-2">
          <div className="font-inter font-500 text-xs sm:text-sm text-muted-foreground">
            Discover More
          </div>
          <Icon
            name="ChevronDown"
            size={24}
            color="var(--color-muted-foreground)"
          />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
